import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
	OrderProduct,
	SetProduct,
	Shopper,
	SetProductWithParentId,
	DinCollectionMethod,
	PartialOrder,
	PartialWithId,
	DepositType,
	RentalState,
} from 'common/types';
import { ReduxState } from 'services/types';
import { alpha, Theme, Typography } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';
import { Divider } from '@mui/material';
import ProductCodeField from './ProductCodeField';
import {
	updateSelectedNonSetProductIds,
	updateSelectedSetProducts,
	updateActivePartialOrder,
	updateActiveShopperId,
} from 'actions';
import classNames from 'classnames';
import DinAndSoleLengthContainer from './DinAndSoleLengthContainer';
import ProductCustomValues from './ProductCustomValues';
import ProductName from './ProductName';
import PriceInput from '../../PriceInput';
import { getPricingString } from 'common/utils/common';
import { CurrencyObject } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import { updateProductListPrice } from 'common/modules/orders/products';
import * as RentalViewSelectors from 'selectors/RentalViewSelectors';

interface StateToProps {
	dinCollectionMethod: DinCollectionMethod;
	showRentalViewFormErrors: boolean;
	depositType: DepositType | undefined;
	rentalState: RentalState;
}

interface OwnProps {
	productToUpdate: OrderProduct;
	product: OrderProduct | SetProduct;
	productId: string;
	setIndex?: number;
	shopper: Shopper;
	setProduct: boolean;
	selectedNonSetProductIds: string[];
	selectedSetProducts: SetProductWithParentId[];
	highlightProduct: boolean;
	editingPrice: boolean;
	editingShopper: boolean;
	currency: CurrencyObject;
	setProductPriceEdit: (data: PartialWithId<OrderProduct>) => void;
}

interface DispatchProps {
	updateSelectedSetProducts: (selectedSetProducts: SetProductWithParentId[]) => void;
	updateSelectedNonSetProductIds: (selectedNonSetProductIds: string[]) => void;
	updateActivePartialOrder: (partialOrder: PartialOrder) => void;
	updateActiveShopperId: (activeShopperId: string | null) => void;
}

type Props = OwnProps & DispatchProps & StateToProps & WithStyles<typeof styles>;

export const ProductItem = (props: Props) => {
	const {
		productToUpdate,
		product,
		shopper,
		classes,
		productId,
		setIndex,
		setProduct,
		selectedSetProducts,
		selectedNonSetProductIds,
		highlightProduct,
		editingPrice,
		editingShopper,
		currency,
		setProductPriceEdit,
		rentalState,
	} = props;
	const [showBackgroundHighlight, setShowBackgroundHighlight] = useState(false);
	const { t } = useTranslation();

	useEffect(() => {
		if (highlightProduct) {
			setShowBackgroundHighlight(true);
		}
		const timer = setTimeout(() => {
			setShowBackgroundHighlight(false);
		}, 900);
		return () => clearTimeout(timer);
	}, [highlightProduct]);

	const isItemSelected = () =>
		(setProduct &&
			!!selectedSetProducts.find(
				(setP) => setP.parentId === productId && setP.productApiId === product.productApiId,
			)) ||
		(!setProduct && selectedNonSetProductIds.includes(productId));

	const allowInteractionForItem = () =>
		!(
			(product as SetProduct).returnedDate ||
			(product as OrderProduct).endDateReturned ||
			(product as SetProduct).removedFromParent ||
			(product as OrderProduct).cancelled ||
			editingShopper
		);

	const handleClick = () => {
		if (allowInteractionForItem()) {
			if (setProduct) {
				const setProductWithParentId: SetProductWithParentId = {
					...(product as SetProduct),
					parentId: props.productId,
				};
				const updatedSelectedSetProducts = selectedSetProducts.filter(
					(setP) =>
						setP.productApiId !== setProductWithParentId.productApiId ||
						setP.parentId !== setProductWithParentId.parentId,
				);
				if (updatedSelectedSetProducts.length === selectedSetProducts.length) {
					updatedSelectedSetProducts.push(setProductWithParentId);
				}
				props.updateSelectedSetProducts(updatedSelectedSetProducts);
			} else {
				const updatedSelectedProducts = selectedNonSetProductIds.filter(
					(p) => p !== props.productId,
				);
				if (updatedSelectedProducts.length === selectedNonSetProductIds.length) {
					updatedSelectedProducts.push(props.productId);
				}
				props.updateSelectedNonSetProductIds(updatedSelectedProducts);
			}
		}
	};

	const getClassNamesForContainer = () => {
		return classNames(
			classes.productSelection,
			showBackgroundHighlight
				? classes.highlightedProduct
				: isItemSelected()
				? classes.selected
				: undefined,
			allowInteractionForItem() ? classes.clickable : undefined,
		);
	};

	const renderProductCustomValues = () => {
		return (
			<ProductCustomValues
				product={product}
				productId={productId}
				setProduct={setProduct}
				setIndex={setIndex}
				shopper={shopper}
				updateActiveShopperId={(activeShopperId: string | null) =>
					props.updateActiveShopperId(activeShopperId)
				}
			/>
		);
	};

	const renderDinAndSoleLengthFields = () => {
		return (
			<DinAndSoleLengthContainer
				productToUpdate={productToUpdate}
				product={product}
				productId={productId}
				setProduct={setProduct}
				setIndex={setIndex}
				shopper={shopper}
				dinCollectionMethod={props.dinCollectionMethod}
				showRentalViewFormErrors={props.showRentalViewFormErrors}
				updateActiveShopperId={(shopperId: string) => props.updateActiveShopperId(shopperId)}
				updateActivePartialOrder={props.updateActivePartialOrder}
			/>
		);
	};

	const renderProductCodeField = () => {
		return (
			<ProductCodeField
				product={product}
				parentProduct={setProduct ? productToUpdate : null}
				productId={productId}
				rentalState={rentalState}
				shopper={shopper}
				isSetProduct={setProduct}
				setIndex={setIndex}
				updateActiveShopperId={(shopperId: string) => props.updateActiveShopperId(shopperId)}
			/>
		);
	};

	const handleUpdateProductPrice = (updatedAmount: number) => {
		const updatedProduct = updateProductListPrice(updatedAmount, product as OrderProduct);
		setProductPriceEdit(updatedProduct.changes);
		return;
	};

	const renderPriceFields = () => {
		if (setProduct) return;
		const pricing = (product as OrderProduct).pricing;
		const isOriginalAmount = pricing.originalListPrice === pricing.listPrice;
		return (
			<>
				<PriceInput
					amount={pricing.listPrice}
					updateProductPrice={handleUpdateProductPrice}
					currency={currency}
				/>
				{!isOriginalAmount && (
					<Typography variant="caption" color="textSecondary" className={classes.helperText}>
						{t('payment.accordingToListPrice', 'List price') +
							': ' +
							getPricingString(pricing.originalListPrice, currency)}
					</Typography>
				)}
			</>
		);
	};

	const handleUpdateProductDeposit = (updatedDeposit: number) => {
		const updatedProduct = product as OrderProduct;
		if (updatedProduct.id && updatedProduct.deposit) {
			const updatedPartialProduct: PartialWithId<OrderProduct> = {
				id: updatedProduct.id,
				data: {
					pricing: {
						...updatedProduct.pricing,
						deposit: updatedDeposit,
					},
				},
			};
			setProductPriceEdit(updatedPartialProduct);
		}
		return;
	};

	return (
		<div>
			<div onClick={handleClick} className={getClassNamesForContainer()}>
				<Grid container spacing={1} alignContent="space-between" alignItems="flex-start">
					<Grid item xs={6} lg={4}>
						<ProductName
							product={props.product}
							allowInteractionForItem={allowInteractionForItem()}
							units={1}
							dinCollectionMethod={props.dinCollectionMethod}
							photoUploaded={shopper.skidataProperties?.photoUploaded}
						/>
					</Grid>
					<Grid
						item
						xs={6}
						lg={8}
						spacing={editingPrice ? 0 : 1}
						container
						justifyContent="flex-end"
					>
						{editingPrice ? (
							renderPriceFields()
						) : (
							<React.Fragment>
								{renderProductCustomValues()}
								{renderDinAndSoleLengthFields()}
								{renderProductCodeField()}
							</React.Fragment>
						)}
					</Grid>
					{editingPrice && !setProduct && (product as OrderProduct).pricing.deposit != null && (
						<>
							<Grid item xs={6} lg={4}>
								<Typography variant="body2">
									{'+ '}
									{t('common:payment.deposit')}
								</Typography>
							</Grid>
							<Grid item xs={6} lg={8} container spacing={0} justifyContent="flex-end">
								<PriceInput
									amount={(product as OrderProduct).pricing.deposit ?? 0}
									updateProductPrice={handleUpdateProductDeposit}
									currency={currency}
								/>
							</Grid>
						</>
					)}
				</Grid>
			</div>
			{!props.setProduct ? <Divider /> : <div />}
		</div>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		productSelection: {
			padding: theme.spacing(1, 0),
			minHeight: theme.spacing(7),
			overflow: 'visible',
		},
		highlightedProduct: {
			backgroundColor: alpha(theme.palette.primary.main, 0.1),
		},
		clickable: {
			'&:hover': {
				cursor: 'pointer',
			},
		},
		selected: {
			backgroundColor: alpha(theme.palette.primary.main, 0.1),
			borderLeft: `solid 5px ${theme.palette.primary.main}`,
			paddingLeft: '11px',
		},
		helperText: {
			marginRight: theme.spacing(2),
		},
	});

const mapStateToProps = (state: ReduxState): StateToProps => {
	const { shop, view } = state;
	const activeShop = shop.shop.data!;
	const showRentalViewFormErrors = view.showRentalViewFormErrors;
	const orderInfo = RentalViewSelectors.orderInfo(state);
	const depositType = orderInfo?.pricing.deposit?.type;
	return {
		dinCollectionMethod: activeShop.shopInfo.dinCollectionMethod || null,
		showRentalViewFormErrors,
		depositType,
		rentalState: orderInfo?.rentalState ?? 'INIT',
	};
};

const dispatchProps = {
	updateSelectedNonSetProductIds,
	updateSelectedSetProducts,
	updateActivePartialOrder,
	updateActiveShopperId,
};

export default withStyles(styles)(connect(mapStateToProps, dispatchProps)(ProductItem));
